import React, { createContext, useContext, useState, ReactNode } from 'react';
import { PaymentMethod } from '../types';

interface PaymentContextType {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod: PaymentMethod | null;
  setSelectedPaymentMethod: (method: PaymentMethod | null) => void;
  paymentStatus: 'idle' | 'processing' | 'success' | 'error';
  setPaymentStatus: (status: 'idle' | 'processing' | 'success' | 'error') => void;
  processPayment: (amount: number, method: PaymentMethod) => Promise<boolean>;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export function PaymentProvider({ children }: { children: ReactNode }) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'credit_card',
      type: 'credit_card',
      name: '信用卡',
      description: 'Visa, MasterCard, JCB',
      icon: 'CreditCard',
    },
    {
      id: 'line_pay',
      type: 'line_pay',
      name: 'LINE Pay',
      description: '使用 LINE Pay 快速付款',
      icon: 'Smartphone',
    },
    {
      id: 'ecpay',
      type: 'ecpay',
      name: '綠界科技',
      description: 'ATM 轉帳、超商代碼',
      icon: 'Building',
    },
  ];

  const processPayment = async (amount: number, method: PaymentMethod): Promise<boolean> => {
    setPaymentStatus('processing');
    try {
      // 這裡會調用實際的支付 API
      // 模擬支付處理
      await new Promise(resolve => setTimeout(resolve, 2000));
      setPaymentStatus('success');
      return true;
    } catch (error) {
      setPaymentStatus('error');
      return false;
    }
  };

  return (
    <PaymentContext.Provider
      value={{
        paymentMethods,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        paymentStatus,
        setPaymentStatus,
        processPayment,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
}

export function usePayment() {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
}
